import {provideAppInitializer, Signal, signal, WritableSignal} from "@angular/core";
import {JsonValue, MaintenanceCfg, type Writable} from "@mindpath/shared";
import {LoggerConfig, LogLevel} from "@mindpath/shared";
import type {ReplayConfiguration} from '@sentry/replay';
import type {
  BrowserClientProfilingOptions,
  BrowserClientReplayOptions,
  ClientOptions
} from "@sentry/core";

export type Environments = 'local' | 'dev' | 'qa' | 'uat' | 'production';
export const Environments = Object.freeze({
  local: 'local',
  dev: 'dev',
  qa: 'qa',
  uat: 'uat',
  production: 'production'
} satisfies {
  readonly [key in Environments]: key
});

class ConfigImp implements Config {

  readonly version!: string;
  readonly appName!: string;
  readonly logging?: LoggerConfig | LogLevel;
  readonly support!: Config['support'];

  public environment: Environments = Environments.local;

  public get isProduction() {
    return this.environment === Environments.production;
  }

  public get isLocal() {
    return this.environment === Environments.local;
  }

  public get isDev() {
    return this.environment === Environments.dev;
  }

  public get isUAT() {
    return this.environment === Environments.uat;
  }

  public get isQA() {
    return this.environment === Environments.qa;
  }

  public readonly endpoints: EndpointsConfig = {} as EndpointsConfig;
  public readonly maintenance: boolean|MaintenanceCfg = false;

  readonly inMaintenance = signal<boolean>(false);

  /** Which support numbers `support` answers with. `ReferralSourceService` owns this. */
  readonly supportProfile = signal<SupportProfile>('default');

  readonly mainSiteBridging = {
    paramNames: {
      insuranceId: 'i',
      planId: 'pl',
      clinicianId: 'cl',
      appointmentDate: 'ad',
      appointmentTime: 'at',
      appointmentSetting: 'as',
      appointmentLocationId: 'al',
      state: 'st',
      schedulingOnBehalfOf: 'sbo'
    }
  };

  readonly finderQueryParamNames: Config['finderQueryParamNames'] = {
    age: "age",
    clinicianType: "clinicianType",
    conditionId: "condition",
    dayOfWeek: "days",
    gender: "gender",
    insuranceId: this.mainSiteBridging.paramNames.insuranceId,
    language: "languages",
    planId: this.mainSiteBridging.paramNames.planId,
    soonestWeekOut: "soonest",
    state: this.mainSiteBridging.paramNames.state,
    timeOfDay: "times",
    visitType: "visitType"
  };

  public loadConfig(json: Writable<Config>) {

    json.support = createSupportProxy(json.support, this.supportProfile);

    Object.assign(this, json);

    this.inMaintenance.update(() => {
      if (typeof this.maintenance === 'boolean') {
        return this.maintenance;
      }
      const inMaintenance = this.maintenance?.enabled;
      if (typeof inMaintenance === 'boolean') {
        return inMaintenance;
      }
      return !!inMaintenance;
    })

  }

  flags: Config['flags'] = {};
}

export type SupportProfile = 'default' | 'referral';

/**
 * Wraps the support-number map so a missing key falls back to `default`, and so a referral visit
 * answers every key with `referralSource`.
 *
 * `profile` is read inside the trap, so a template rendering `config.support.x` tracks it and
 * re-renders when a referral entry is applied or cleared.
 */
export function createSupportProxy(support: Config['support'], profile: Signal<SupportProfile>): Config['support'] {
  return new Proxy(support, {
    get: (target, prop) => {
      // No support key is a symbol, and interpolating one below would throw.
      if (typeof prop !== 'string') {
        return Reflect.get(target, prop);
      }
      if (profile() === 'referral' && target.referralSource?.length) {
        return target.referralSource;
      }
      let num: string | undefined = undefined;
      if (prop in target) {
        num = target[prop as keyof Config['support']];
      }
      if (!num || !num.length) {
        num = target.default;
        console.error(`Support number for "${prop}" is not defined, using default: ${num}`);
      }
      return num;
    }
  });
}

const configImp = new ConfigImp();
export const config: Config = configImp;

let loaded = false;
export async function LoadConfig() {
  if (loaded) {
    return config;
  }
  const response = await fetch('/config.json');
  const json = await response.json();
  loaded = true;
  console.log('config', json);
  configImp.loadConfig(json);
  return config;
}

export function provideConfig() {
  return provideAppInitializer(() => LoadConfig())
}


export interface Config {
  readonly version: string;
  readonly isProduction: boolean;
  readonly isUAT: boolean;
  readonly isQA: boolean;
  readonly isDev: boolean;
  readonly isLocal: boolean;
  readonly environment: Environments;
  readonly endpoints: EndpointsConfig;
  readonly maintenance: MaintenanceCfg|boolean;
  readonly inMaintenance: WritableSignal<boolean>
  readonly supportProfile: WritableSignal<SupportProfile>;
  readonly appName: string;
  readonly support: {
    default: string,
    schedulingForSomeoneElse: string,
    landingPage: string,
    ageRangeFilter: string,
    selfPayFilter: string,
    dontSeeInsuranceFilter: string,
    selfPayResultsFilter: string,
    dontSeeInsuranceResultsFilter: string,
    doNotScheduleEligibility: string,
    needsVerificationEligibility: string,
    under18: string,
    selfPayForm: string,
    dontSeeInsuranceForm: string,
    unacceptedInsurance: string,
    doNotScheduleInsurance: string,
    needsVerificationInsurance: string,
    delayedVerifyInsurance: string,
    /** Every number on a referral visit (`pks=MPRF-{guid}`). */
    referralSource: string,
  }
  readonly logging?: LoggerConfig|LogLevel;
  readonly piwik?: {
    siteId: string;
    url: string;
  }
  readonly sentry?: {
    dsn: string;
  } & Partial<ReplayConfiguration> & Partial<BrowserClientReplayOptions> & Partial<BrowserClientProfilingOptions> & Partial<ClientOptions>;
  readonly mainSiteBridging: {
    paramNames: {
      insuranceId: string,
      planId: string,
      clinicianId: string,
      appointmentDate: string,
      appointmentTime: string,
      appointmentSetting: string,
      appointmentLocationId: string,
      state: string,
      schedulingOnBehalfOf: string
    }
  },
  readonly flags: Record<string, JsonValue>,

  readonly finderQueryParamNames: {
    state: string,
    clinicianType: string,
    conditionId: string,
    age: string,
    visitType: string,
    insuranceId: string,
    planId: string,
    language: string,
    gender: string,
    timeOfDay: string,
    dayOfWeek: string,
    soonestWeekOut: string
  }
}

export interface EndpointsConfig {
  api: string;
  mainSite: string;
}

